import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { avatarSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Heart, Send, Trash2, Loader2, Lock, CornerDownRight, Award } from "lucide-react";
import { toast } from "sonner";
import { VerifiedBadge } from "./VerifiedBadge";
import { DonorBadge } from "./DonorBadge";
import { OnlineDot } from "./OnlineDot";
import { MentionInput } from "./MentionInput";
import { MentionText } from "./MentionText";

function timeAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

function Avatar({ src, name, size = 32, username }) {
  const img = src
    ? <img src={avatarSrc(src)} alt={name} style={{ width: size, height: size }} className="rounded-full object-cover bg-[#141414] shrink-0" />
    : <div style={{ width: size, height: size }} className="rounded-full bg-[#141414] flex items-center justify-center text-[#FFFFFF] text-sm font-semibold shrink-0">{name?.[0]?.toUpperCase() || "?"}</div>;
  return <span className="relative shrink-0">{img}{username && <OnlineDot username={username} size={9} />}</span>;
}

const renderText = (text) => <MentionText text={text} />;

function CommentRow({ c, canDelete, onLike, onReply, onDelete, isReply, isTop }) {
  return (
    <div className={`flex items-start gap-2 group ${isReply ? "mt-2" : ""}`} data-testid={`comment-${c.id}`}>
      <Avatar src={c.avatar_url} name={c.display_name || c.username} size={isReply ? 28 : 32} username={c.username} />
      <div className="flex-1 min-w-0">
        {isTop && (
          <div data-testid={`comment-top-badge-${c.id}`} className="inline-flex items-center gap-1 mb-1 px-2 py-0.5 rounded-full bg-[#E60023]/15 text-[#FF5C7A] text-[10px] font-bold uppercase tracking-wider">
            <Award size={11} /> Top comment
          </div>
        )}
        <div className="bg-[#141414] rounded-2xl px-3 py-2 inline-block max-w-full">
          <Link to={`/u/${c.username}`} data-testid={`comment-author-${c.id}`} className="flex items-baseline gap-1.5 min-w-0 group/author">
            <span className="flex items-center gap-1 min-w-0">
              <span data-testid={`comment-author-name-${c.id}`} className="text-[13px] font-semibold text-[#FFFFFF] group-hover/author:underline truncate">{c.display_name || c.username}</span>
              <VerifiedBadge show={c.verified} size={13} testId={`comment-verified-${c.id}`} />
              <DonorBadge tier={c.donor_tier} size={12} testId={`comment-donor-${c.id}`} />
            </span>
            <span data-testid={`comment-author-username-${c.id}`} className="text-[11px] text-[#8A8D91] truncate">@{c.username}</span>
          </Link>
          <p className="text-sm text-[#FFFFFF] break-words whitespace-pre-wrap">{renderText(c.text)}</p>
        </div>
        <div className="flex items-center gap-4 mt-0.5 ml-3 text-[11px] font-semibold">
          <button data-testid={`comment-like-${c.id}`} onClick={() => onLike(c)} className={`flex items-center gap-1 transition-colors ${c.liked ? "text-[#E60023]" : "text-[#52525B] hover:text-[#FFFFFF]"}`}>
            <Heart size={13} fill={c.liked ? "#E60023" : "none"} /> {c.likes_count > 0 ? c.likes_count : "Like"}
          </button>
          <button data-testid={`comment-reply-${c.id}`} onClick={() => onReply(c)} className="text-[#52525B] hover:text-[#FFFFFF] transition-colors">Reply</button>
          <span className="text-[#52525B] font-normal">{timeAgo(c.created_at)}</span>
        </div>
      </div>
      {canDelete && (
        <button data-testid={`comment-delete-${c.id}`} onClick={() => onDelete(c)} className="opacity-0 group-hover:opacity-100 text-[#52525B] hover:text-[#E60023] transition-all p-1 shrink-0"><Trash2 size={13} /></button>
      )}
    </div>
  );
}

export default function CommentThread({ mediaId, locked = false, onCountChange }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [comments, setComments] = useState(null); // null = loading
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // comment being replied to
  const [replyText, setReplyText] = useState("");

  useEffect(() => {
    let alive = true;
    api.get(`/media/${mediaId}/comments`).then(({ data }) => alive && setComments(data)).catch(() => alive && setComments([]));
    return () => { alive = false; };
  }, [mediaId]);

  const staff = user && (user.role === "admin" || user.role === "moderator");
  const canDelete = (c) => user && (c.user_id === user.id || staff);

  const tree = useMemo(() => {
    const list = comments || [];
    const tops = list.filter((c) => !c.parent_id).sort((a, b) => (b.likes_count - a.likes_count) || (new Date(b.created_at) - new Date(a.created_at)));
    const byParent = {};
    list.filter((c) => c.parent_id).forEach((c) => { (byParent[c.parent_id] ||= []).push(c); });
    Object.values(byParent).forEach((arr) => arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
    // the single most-liked top-level comment (needs at least 2 likes to stand out)
    const topId = tops.length > 1 && tops[0].likes_count >= 2 ? tops[0].id : null;
    return { tops, byParent, topId };
  }, [comments]);

  const post = async (e, parentId = null, value, done) => {
    e?.preventDefault?.();
    if (!user) return nav("/login");
    const t = (value || "").trim();
    if (!t) return;
    try {
      const { data } = await api.post(`/media/${mediaId}/comments`, { text: t, parent_id: parentId });
      setComments((c) => [data, ...(c || [])]);
      onCountChange?.(1);
      done?.();
    } catch (err) {
      toast.error(err.response?.status === 403 ? "Comments are locked on this post" : "Couldn't post comment");
    }
  };

  const submitTop = async (e) => { setPosting(true); await post(e, null, text, () => setText("")); setPosting(false); };
  const submitReply = async (e) => { await post(e, replyTo.id, replyText, () => { setReplyText(""); setReplyTo(null); }); };

  const like = async (c) => {
    if (!user) return nav("/login");
    try {
      const { data } = await api.post(`/comments/${c.id}/like`);
      setComments((list) => list.map((x) => x.id === c.id ? { ...x, liked: data.liked, likes_count: data.likes_count } : x));
    } catch { /* noop */ }
  };

  const del = async (c) => {
    try {
      await api.delete(`/comments/${c.id}`);
      // remove the comment and any replies to it
      setComments((list) => list.filter((x) => x.id !== c.id && x.parent_id !== c.id));
      const removed = 1 + (tree.byParent[c.id]?.length || 0);
      onCountChange?.(-removed);
    } catch { /* noop */ }
  };

  return (
    <div data-testid={`comment-thread-${mediaId}`}>
      {locked ? (
        <div data-testid="comments-locked" className="flex items-center gap-2 text-[#A1A1AA] text-sm bg-[#141414]/40 border border-[#1C1C1F] rounded-lg px-4 py-3 mb-3"><Lock size={14} /> Comments are locked on this post.</div>
      ) : user ? (
        <form onSubmit={submitTop} className="flex items-center gap-2 mb-3">
          <Avatar src={user.avatar_url} name={user.username} size={32} />
          <MentionInput wrapperClassName="flex-1 min-w-0" up data-testid={`comment-input-${mediaId}`} value={text} onChange={setText} placeholder="Write a comment…" className="w-full bg-[#141414] rounded-full px-4 py-2 text-sm text-[#FFFFFF] placeholder:text-[#52525B] focus:outline-none focus:ring-1 focus:ring-[#0066FF]" />
          <button data-testid={`comment-submit-${mediaId}`} disabled={posting || !text.trim()} className="w-9 h-9 rounded-full bg-[#0066FF] hover:bg-[#0055CC] disabled:opacity-40 flex items-center justify-center text-white shrink-0 transition-colors">{posting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}</button>
        </form>
      ) : (
        <button onClick={() => nav("/login")} className="text-sm text-[#4599FF] hover:underline mb-3">Log in to join the conversation</button>
      )}

      {comments === null ? (
        <div className="flex justify-center py-3"><Loader2 className="animate-spin text-[#0066FF]" size={18} /></div>
      ) : tree.tops.length === 0 ? (
        <p className="text-[#52525B] text-sm py-1">No comments yet — be the first.</p>
      ) : (
        <div className="space-y-4">
          {tree.tops.map((c) => (
            <div key={c.id}>
              <CommentRow c={c} canDelete={canDelete(c)} onLike={like} onReply={setReplyTo} onDelete={del} isTop={c.id === tree.topId} />
              {/* replies */}
              {(tree.byParent[c.id] || []).length > 0 && (
                <div className="ml-8 pl-3 border-l border-[#1C1C1F] mt-1">
                  {tree.byParent[c.id].map((r) => (
                    <CommentRow key={r.id} c={r} canDelete={canDelete(r)} onLike={like} onReply={() => setReplyTo(c)} onDelete={del} isReply />
                  ))}
                </div>
              )}
              {/* reply composer */}
              {replyTo?.id === c.id && !locked && (
                <form onSubmit={submitReply} className="flex items-center gap-2 ml-8 mt-2" data-testid={`reply-form-${c.id}`}>
                  <CornerDownRight size={15} className="text-[#52525B] shrink-0" />
                  <Avatar src={user?.avatar_url} name={user?.username} size={26} />
                  <MentionInput wrapperClassName="flex-1 min-w-0" up autoFocus data-testid={`reply-input-${c.id}`} value={replyText} onChange={setReplyText} placeholder={`Reply to @${c.username}…`} className="w-full bg-[#141414] rounded-full px-4 py-1.5 text-sm text-[#FFFFFF] placeholder:text-[#52525B] focus:outline-none focus:ring-1 focus:ring-[#0066FF]" />
                  <button data-testid={`reply-submit-${c.id}`} disabled={!replyText.trim()} className="w-8 h-8 rounded-full bg-[#0066FF] hover:bg-[#0055CC] disabled:opacity-40 flex items-center justify-center text-white shrink-0"><Send size={14} /></button>
                  <button type="button" onClick={() => { setReplyTo(null); setReplyText(""); }} className="text-[#52525B] text-xs hover:text-[#FFFFFF]">Cancel</button>
                </form>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
